import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import * as Sentry from '@sentry/browser';
import { toast } from 'react-toastify';

import IconButton from '../../molecules/icon-button';
import IconHeart from '../../atoms/icon-heart';

import { createWishedProduct, deleteWishedProduct } from '../../pages/api';
import { addProductToList, removeProductFromList } from '../../store/modules/lists/actions';

const Heart = styled(IconHeart)(({ theme }) => ({
  fill: theme.color.white,
  stroke: theme.color.brand,
  '&.is-active': {
    fill: theme.color.brand
  }
}));

const Wishlist = styled(IconButton)(({ theme }) => ({
  margin: '0 auto',
  border: 0,
  outline: 0,
  height: '3rem',
  lineHeight: '2.8rem',
  [Heart]: {
    height: '2.5rem',
    marginRight: '0.25rem',
    width: '2.5rem'
  },
  [`@media screen and (min-width: ${theme.breakpoint.medium})`]: {
    background: 'rgba(255, 255, 255, 0.65)',
    borderRadius: '100%',
    display: 'block',
    height: '4rem',
    opacity: 0,
    position: 'absolute',
    right: '1rem',
    top: '1rem',
    width: '4rem',
    transition: `opacity ${theme.animation.slow} ${theme.animation.easeOutQuad},
      transform ${theme.animation.default} ${theme.animation.easeOutQuad}`,
    [Heart]: {
      height: '4rem',
      margin: 0,
      transform: 'scale(0.75)',
      width: '4rem'
    },
    i: { display: 'none' },
    ':hover': {
      transform: 'scale(1.15)'
    }
  }
}));

const WishlistButton = ({ product, ...props }) => {
  const dispatch = useDispatch();
  const [wished_products, spree_api_key] = useSelector(
    (store) => [store.lists.wished_products, store.user.spree_api_key]
  );

  const isProductInWishlist = () => wished_products.some((p) => p.variant_id === +product.objectID);

  const [active, setActive] = useState(isProductInWishlist(product));
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    setActive(isProductInWishlist());
  }, [wished_products]);

  const hasAuthError = (errors = []) => {
    if (!spree_api_key) return true;

    try {
      return errors.find(({ code }) => code === 401);
    } catch (error) {
      Sentry.captureException(error);
      return true;
    }
  };

  const add = async () => {
    setDisabled(true);

    await createWishedProduct({
      body: { wished_product: { variant_id: +product.objectID } }
    }).then((res) => {
      if (res.errors) {
        const isUnauthorized = hasAuthError(res.errors);

        if (isUnauthorized) {
          toast(
            <p>
            You must be
              <a href="/login">signed in</a>
              {' '}
              to add a product to your wishlist
            </p>,
            { type: toast.TYPE.INFO }
          );
        } else if (res.errors.find(({ message }) => message?.includes('has already been taken'))) {
          toast('This product has already been added to your wishlist, please refresh the page for updates', { type: toast.TYPE.ERROR });
        } else {
          toast('Something went wrong, please try again later', { type: toast.TYPE.ERROR });
        }
      }

      dispatch(addProductToList(res));
    }).catch((error) => {
      Sentry.captureException(error);
    });

    setDisabled(false);
  };

  const remove = async () => {
    setDisabled(true);

    const { id } = wished_products.find((p) => p.variant_id === +product.objectID);

    await deleteWishedProduct({ id }).then(() => {
      dispatch(removeProductFromList(id));
    }).catch((error) => {
      Sentry.captureException(error);
    });

    setDisabled(false);
  };

  const onWishlistClicked = (e) => {
    // prevent wishlist from triggering anchor
    e.stopPropagation();
    if (active) remove();
    else add();
  };

  return (
    <Wishlist
      aria-label={
        active
          ? `remove "${product.title}", from wishlist`
          : `add "${product.title}", to wishlist`
      }
      text="Wishlist"
      onClick={onWishlistClicked}
      disabled={disabled}
      {...props}
    >
      <Heart className={active ? 'is-active' : undefined} />
    </Wishlist>
  );
};

WishlistButton.propTypes = {
  product: PropTypes.object.isRequired
};

export default WishlistButton;
